import { CurrencySelect, LanguageSelect, UpsellGuard } from '@components'

import { Flex, FormItemsGroup, Icon, Select, Spacing } from '@billy/design-system'
import type { ReactElement } from 'react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { selectableInvoiceLanguages } from '@features/invoices/constants/selectableInvoiceLanguages'
import { useInvoiceMultiCurrencyAccess } from '@features/invoices/hooks/useInvoiceMultiCurrencyAccess'
import type { ProductType } from '@features/products/enums/productType'

import { Scope } from '../../../../../../../../enums/scope'
import { TrackingContext } from '../../../../../../../../enums/trackingContext'
import { useFormContext } from '../../../../../../../../hooks/useForm'
import { InvoicingSettingsSection } from '../../../../../../enums/invoicingSettingsSection'
import { useTrackInvoicingSettings } from '../../../../../../hooks/useTrackInvoicingSettings'
import type { PresetsSettingsFormSchema } from '../../types/presetsSettingsFormSchema'
import { getProductTypeItems } from './utils/getProductTypeItems'

export const PresetsDocumentDefaults = (): ReactElement => {
  const { t } = useTranslation()
  const { FormItem } = useFormContext<PresetsSettingsFormSchema>()
  const { shouldDisplayInvoiceMultiCurrency } = useInvoiceMultiCurrencyAccess()
  const { trackInvoicingSettings } = useTrackInvoicingSettings({ section: InvoicingSettingsSection.Presets })

  const productTypeItems = useMemo(() => getProductTypeItems(t), [t])

  const languageField = (
    <FormItem
      label={t('settings.organization.external_invoice.general.default_language.label')}
      name="language"
      render={({ field: { onChange, value, ...rest } }) => (
        <LanguageSelect
          {...rest}
          languages={selectableInvoiceLanguages}
          onSelect={(language) => {
            onChange(language)
            trackInvoicingSettings('Language dropdown selected', { language })
          }}
          selectedId={value}
          withSearch={false}
        />
      )}
    />
  )
  const productTypeField = (
    <FormItem
      label={t('settings.organization.external_invoice.general.default_product_type.label')}
      name="productType"
      render={({ field: { onChange, value, ...rest } }) => (
        <Select<ProductType>
          {...rest}
          dropdownSize="fitTrigger"
          items={productTypeItems}
          onSelect={(_id, selectedProductType) => {
            onChange(selectedProductType)
            trackInvoicingSettings('Default Item Type selected', { type: selectedProductType })
          }}
          selectedId={value}
          withSearch={false}
        />
      )}
    />
  )

  // Currency takes the second cell, so item type keeps its own full-width row.
  // Without currency, language and item type share that row.
  if (!shouldDisplayInvoiceMultiCurrency) {
    return (
      <FormItemsGroup columnSpacing={Spacing.S16} itemsInRow={2} rowSpacing={Spacing.S16}>
        {languageField}
        {productTypeField}
      </FormItemsGroup>
    )
  }

  return (
    <Flex flexDirection="column" gap={Spacing.S16}>
      <FormItemsGroup columnSpacing={Spacing.S16} itemsInRow={2} rowSpacing={Spacing.S16}>
        {languageField}
        <FormItem
          label={t('settings.organization.external_invoice.general.default_currency.label')}
          name="currency"
          render={({ field: { onChange, value, ...rest } }) => (
            <UpsellGuard
              scope={Scope.InvoiceMultiCurrencyUpsell}
              trackingContext={TrackingContext.InvoiceMultiCurrency}
            >
              <CurrencySelect
                {...rest}
                dropdownSize="fitTrigger"
                onSelect={(currency) => {
                  onChange(currency)
                  trackInvoicingSettings('Currency dropdown selected', { currency })
                }}
                prefix={<Icon icon="coins" />}
                selectedId={value}
              />
            </UpsellGuard>
          )}
        />
      </FormItemsGroup>
      <FormItemsGroup>{productTypeField}</FormItemsGroup>
    </Flex>
  )
}
